import { useState, useEffect, useCallback } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { toast } from "react-hot-toast";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { capture } from "../instrument";
import API from "../api";

const useSavedViews = ({ collectionName, defaultViews, activeFilters, setActiveFilters, initialViewId }) => {
  const queryClient = useQueryClient();
  const [activeViewId, setActiveViewId] = useState(initialViewId || defaultViews[0]._id);
  const [saveModalOpen, setSaveModalOpen] = useState(false);

  const { data: savedViews = [], isError: isSavedViewsError, error: savedViewsError } = useQuery({
    queryKey: ["saved-views", collectionName],
    queryFn: async () => {
      const res = await API.get(`/saved-view?collectionName=${collectionName}`);
      if (!res.ok) throw res;
      return res.data;
    },
    staleTime: Infinity,
  });

  useEffect(() => {
    if (!isSavedViewsError) return;
    capture(savedViewsError);
    toast.error("Erreur lors du chargement des vues");
  }, [isSavedViewsError]);

  const handleSelectView = useCallback(
    (tab) => {
      setActiveFilters(tab.filters || []);
      setActiveViewId(tab._id);
    },
    [setActiveFilters],
  );

  const isViewActive = (tab) => activeViewId === tab._id;

  const updateViewMutation = useMutation({
    mutationFn: (viewId) => API.put(`/saved-view/${viewId}`, { filters: activeFilters }),
    onSuccess: (res) => {
      if (!res.ok) return toast.error("Erreur lors de la mise a jour de la vue");
      queryClient.invalidateQueries({ queryKey: ["saved-views", collectionName] });
      toast.success("Vue mise a jour");
    },
    onError: (error) => {
      capture(error);
      toast.error("Erreur lors de la mise a jour de la vue");
    },
  });

  const createViewMutation = useMutation({
    mutationFn: (name) => API.post("/saved-view", { name, collectionName, filters: activeFilters }),
    onSuccess: (res) => {
      if (!res.ok) return toast.error("Erreur lors de l'enregistrement de la vue");
      queryClient.invalidateQueries({ queryKey: ["saved-views", collectionName] });
      setActiveViewId(res.data._id);
      toast.success("Vue enregistree");
    },
    onError: (error) => {
      capture(error);
      toast.error("Erreur lors de l'enregistrement de la vue");
    },
  });

  const deleteViewMutation = useMutation({
    mutationFn: (viewId) => API.delete(`/saved-view/${viewId}`),
    onSuccess: (res, viewId) => {
      if (!res.ok) return toast.error("Erreur lors de la suppression de la vue");
      queryClient.invalidateQueries({ queryKey: ["saved-views", collectionName] });
      if (activeViewId === viewId) {
        setActiveFilters([]);
        setActiveViewId(defaultViews[0]._id);
      }
    },
    onError: (error) => {
      capture(error);
      toast.error("Erreur lors de la suppression de la vue");
    },
  });

  const handleConfirmSaveView = (name) => {
    createViewMutation.mutate(name);
  };

  const handleDeleteView = (viewId) => {
    const view = savedViews.find((v) => v._id === viewId);
    const confirmed = window.confirm(`Supprimer la vue "${view?.name}" ?`);
    if (!confirmed) return;
    deleteViewMutation.mutate(viewId);
  };

  const isOnSavedView = activeViewId && !defaultViews.some((t) => t._id === activeViewId);
  const activeView = isOnSavedView ? savedViews.find((v) => v._id === activeViewId) : null;

  const saveOptions = isOnSavedView
    ? [
        { label: `Mettre a jour "${activeView?.name}"`, onClick: () => updateViewMutation.mutate(activeViewId) },
        { label: "Nouvelle vue", onClick: () => setSaveModalOpen(true) },
      ]
    : [{ label: "Enregistrer", onClick: () => setSaveModalOpen(true) }];

  useHotkeys(
    ["mod+1", "mod+2", "mod+3", "mod+4", "mod+5", "mod+6", "mod+7", "mod+8", "mod+9"],
    (e) => {
      const allTabs = [...defaultViews, ...savedViews];
      const tabIndex = Number(e.key) - 1;
      if (tabIndex >= allTabs.length) return;
      handleSelectView(allTabs[tabIndex]);
    },
    { preventDefault: true, enableOnFormTags: true },
    [defaultViews, savedViews, handleSelectView],
  );

  return {
    savedViews,
    activeViewId,
    saveModalOpen,
    setSaveModalOpen,
    handleSelectView,
    isViewActive,
    handleConfirmSaveView,
    handleDeleteView,
    isOnSavedView,
    saveOptions,
  };
};

export default useSavedViews;
